import React, { useState, useEffect } from 'react';
import { WCFDA } from '../../types/wordpress';
import { WordPressAPI } from '../../services/api/api';
import FDAForm from './FDAForm';
import { Button, Table, Modal, message, Popconfirm, Tag, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, UndoOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';

const FDAList: React.FC = () => {
    const [fdaItems, setFDAItems] = useState<WCFDA[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingFDA, setEditingFDA] = useState<WCFDA | null>(null);
    const [searchText, setSearchText] = useState('');
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchFDAItems();
    }, [currentPage, pageSize, searchText]);

    const fetchFDAItems = async () => {
        try {
            const data = await WordPressAPI.getFDAs({
                page: currentPage,
                per_page: pageSize,
                search: searchText
            });
            setFDAItems(data.items);
            setTotal(data.total);
        } catch (error) {
            message.error('Failed to fetch FDA items');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredFDAItems = () => {
        return fdaItems.filter(item =>
            item.name.toLowerCase().includes(searchText.toLowerCase()) ||
            item.address.toLowerCase().includes(searchText.toLowerCase()) ||
            (item.registration_number?.toLowerCase().includes(searchText.toLowerCase()) ?? false)
        );
    };

    const handleDownload = (url: string, filename: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSoftDelete = async (id: number) => {
        try {
            await WordPressAPI.updateFDA(id, { is_deleted: true });
            message.success('FDA registration deactivated successfully');
            fetchFDAItems();
        } catch (error) {
            message.error('Failed to deactivate FDA registration');
            console.error(error);
        }
    };

    const handleRestore = async (id: number) => {
        try {
            await WordPressAPI.updateFDA(id, { is_deleted: false });
            message.success('FDA registration restored successfully');
            fetchFDAItems();
        } catch (error) {
            message.error('Failed to restore FDA registration');
            console.error(error);
        }
    };



    const handleEdit = (fda: WCFDA) => {
        setEditingFDA(fda);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingFDA(null);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingFDA(null);
    };

    const columns: ColumnsType<WCFDA> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            sorter: (a, b) => a.address.localeCompare(b.address),
        },
        {
            title: 'Telephone',
            dataIndex: 'telephone',
            key: 'telephone',
        },
        {
            title: 'Registration Number',
            dataIndex: 'registration_number',
            key: 'registration_number',
            sorter: (a, b) => (a.registration_number ?? '').localeCompare(b.registration_number ?? ''),
        },
        {
            title: 'Expiry Date',
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            sorter: (a, b) => {
                const dateA = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
                const dateB = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
                return dateA - dateB;
            },
            render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
        {
            title: 'Attachments',
            key: 'attachments',
            width: '200px',
            render: (_, record) => (
                <div>
                    {record.attachments?.map((attachment, index) => (
                        <Button
                            key={index}
                            type="link"
                            icon={<DownloadOutlined />}
                            title={attachment.name} // This adds the hover tooltip
                            onClick={() => handleDownload(attachment.url, attachment.name)}
                        >
                            {attachment.name.length > 20
                                ? `${attachment.name.slice(0, 16)}...${attachment.name.slice(attachment.name.lastIndexOf('.'))}`
                                : attachment.name}

                        </Button>
                    ))}
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
                { text: 'Expired', value: 'expired' },
            ],
            onFilter: (value, record) => {
                if (value === 'expired') {
                    return record.expiry_date ? new Date(record.expiry_date) < new Date() : false;
                }
                if (value === 'active') {
                    return !record.is_deleted && (!record.expiry_date || new Date(record.expiry_date) >= new Date());
                }
                return record.is_deleted;
            },
            render: (_, record) => {
                if (record.is_deleted) {
                    return <Tag color="red">Inactive</Tag>;
                }
                return record.expiry_date && new Date(record.expiry_date) < new Date() ?
                    <Tag color="orange">Expired</Tag> :
                    <Tag color="green">Active</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="action-buttons">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this FDA registration?"
                            onConfirm={() => handleRestore(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                className="restore-button"
                            >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this FDA registration?"
                            onConfirm={() => handleSoftDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="delete-button"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full p-4">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">FDA Registrations</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                        placeholder="Search FDA registrations..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-full sm:w-[300px]"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                        className="whitespace-nowrap"
                    >
                        Add New Registration
                    </Button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table
                    columns={columns}
                    dataSource={fdaItems}
                    loading={loading}
                    rowKey="id"
                    rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                    pagination={{
                        total: total,
                        current: currentPage,
                        pageSize: pageSize,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <Modal
                title={editingFDA ? 'Edit FDA Registration' : 'Add New FDA Registration'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                className="responsive-modal"
                destroyOnClose={true}
            >
                <FDAForm
                    key={editingFDA?.id || 'new'}
                    initialValues={editingFDA}
                    onSubmit={async (values) => {
                        try {
                            if (editingFDA) {
                                await WordPressAPI.updateFDA(editingFDA.id, values);
                                message.success('FDA registration updated successfully');
                            } else {
                                await WordPressAPI.createFDA(values);
                                message.success('FDA registration created successfully');
                            }
                            handleModalClose();
                            fetchFDAItems();
                        } catch (error) {
                            message.error('Failed to save FDA registration');
                            console.error(error);
                        }
                    }}
                    onCancel={handleModalClose}
                />
            </Modal>
        </div>
    );
};

export default FDAList; 